import { BusinessEntity, BusinessEntityService } from '@/@types/generated'
import Loading from '@/components/Atoms/Loading'
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react'

interface CompanyContextProps {
  company: BusinessEntity
  reload: (background?: boolean) => Promise<void>
}

const CompanyContext = createContext<CompanyContextProps>(undefined!)

export function useCompany() {
  const context = useContext(CompanyContext)
  if (context === undefined) {
    throw new Error(
      'useCompany must be used within a CompanyContext.Provider route'
    )
  }

  return context
}

export function CompanyProvider({
  id,
  children,
  onError,
}: React.PropsWithChildren<{
  id: BusinessEntity['id']
  onError?: (error: unknown) => void
}>) {
  // State
  const [loading, setLoading] = useState(true)
  const [company, setCompany] = useState<BusinessEntity | null>(null)

  // Load the company details
  const reload = useCallback(
    async (background?: boolean) => {
      if (!id) return
      if (!background) setLoading(true)

      try {
        const company = await BusinessEntityService.get(id)
        setCompany(company)
      } catch (error) {
        onError?.(error)
      } finally {
        setLoading(false)
      }
    },
    [id, onError]
  )

  // Load company details when ID changes
  useEffect(() => {
    reload()
  }, [reload])

  // No company yet, show loading
  if (loading)
    return (
      <Loading variant="fullscreen" message="Retrieving company details..." />
    )
  if (!company) return <div>Company not found</div>

  return (
    <CompanyContext.Provider
      value={{
        company,
        reload,
      }}
    >
      {children}
    </CompanyContext.Provider>
  )
}
